/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  ActionConfig,
  ActionDescription,
  Display_DisplayOptional,
} from "@smartsuite/automation-engine-api";
import { RecordListInputConfig_Reference } from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import { reaction } from "mobx";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import * as R from "remeda";
import { isTruthy } from "remeda";
import { Skeleton } from "../../../../../generated/images";
import type { AuthAttempt } from "../../../../auth";
import { authDispatcher } from "../../../../auth";
import { useEngineClient } from "../../../../services/engineClient";
import { checkActionDescriptionError } from "../../../../services/updateDescription";
import * as A from "../../../../stores/automation/mergers/actions";
import { useRootStore } from "../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
} from "../../../../types/candidate";
import {
  ACTIONS_SKIP_MERGING,
  INPUT_APPLICATION_ID,
  INPUT_SOLUTION_ID,
  Integrations,
} from "../../../../utils/constants";
import { pickValueDataString } from "../../../../utils/inputs";
import { shouldShowActionInputValidationErrors } from "../../../../utils/action-helpers";
import { RecordsSourceEditor } from "../../Forms/InputForm/RecordsSourceEditor";
import { ActionSelectorMenu } from "../../Selectors/ActionIntegrationSelector";
import {
  isCredentialIdEmpty,
  NO_CREDS,
} from "../../Selectors/CredentialSelector/no-creds";
import { ActionSectionWrapper, SectionHeading } from "../../style";
import { ConditionsForm } from "../TriggerSection/ConditionsForm";
import { ActionCredentialSelector } from "./ActionCredentialSelection";
import { ActionTestSection } from "./ActionTestSection";
import { AlwaysFieldList } from "./AlwaysFieldList";
import { GroupFieldList } from "./GroupFieldList";
import {
  buildInitialInputs,
  withStoreSelectOptions,
} from "./initial-action-description";
import { getDependentInputsToClear } from "./dependent-inputs";
import { StyledActionInputsContainer, StyledIconCloseContainer } from "./style";
import { useFindActionAuthenticationIds } from "../find-action-authentication-ids";

export interface OptionalFields extends Display_DisplayOptional {
  inputs: InputCandidate[];
}

export const ActionSection = observer<{
  actionHash: string;
  setAuthAttempt: (auth: AuthAttempt) => void;
  onChangeAction: (integrationId: string, actionId: string) => void;
  setIsLoading: (status: boolean) => void;
  onClickTest: (path?: string) => void;
  path?: string;
}>(
  ({
    actionHash,
    setAuthAttempt,
    onChangeAction,
    setIsLoading,
    onClickTest,
    path,
  }) => {
    const store = useRootStore();
    const engineClient = useEngineClient();
    const { automations } = store;

    const { candidate } = automations;
    const actions = candidate.allActions;
    const applications = store.applications.list;
    const [loading, setLoading] = useState<boolean>(false);
    const [isSelectOpened, setIsSelectOpened] = useState(false);
    const [placeAfterId, setPlaceAfterId] = useState<string | undefined>(
      undefined,
    );
    const findActionAuthenticationIds = useFindActionAuthenticationIds();

    // Actions that authenticate transparently as SmartSuite auto-create a
    // system-user credential, so no credential picker is shown. Every such
    // integration (smartsuite, o365, ms-teams, slack, and any future one)
    // declares the shared "ss-auth" method on the action, so gate on the
    // action's own auth method rather than a per-integration allowlist.
    const usesTransparentSsAuth = (action?: ActionCandidate): boolean =>
      action !== undefined &&
      findActionAuthenticationIds(action).includes("ss-auth");

    const actionGroupType =
      candidate.currentActionGroup?.config.oneofKind ?? "actions";

    // set both inner and parent loading status
    const setLoadingStatus = (status: boolean): void => {
      setLoading(status);
      setIsLoading(status);
    };

    const onClickSelect = useCallback(() => {
      setIsSelectOpened(!isSelectOpened);
    }, [isSelectOpened]);

    const onClickOutsideSelect = useCallback(() => {
      setIsSelectOpened(false);
    }, [setIsSelectOpened]);

    const updateActionDescription = (action: ActionCandidate): void => {
      store.automations.setLoadingDynamicDescription(true);

      const currentAction = R.find(
        candidate.allActions,
        (a) => a.hash === action.hash,
      );

      if (currentAction?.actionReference?.instanceId === undefined) {
        return;
      }

      void engineClient
        .getDynamicActionDescription({
          action: R.find(candidate.allActions, (a) => a.hash === action.hash),
          solutionId: candidate.self.solutionId,
          automationId: candidate.self.automationId,
        })
        .then(({ action: description }) => {
          description && storeActionDescription(description, action);
          candidate.setActions(
            [
              A.mergeActionDeletedError(action.hash, false),
              A.mergeActionAccessError(action.hash, false),
            ],
            actionGroupType,
          );
        })
        .catch((error) => {
          checkActionDescriptionError(action, candidate, error);
        })
        .finally(() => {
          automations.setLoadingDynamicDescription(false);
          automations.setRefreshingList(false);
        });
    };

    // Auto login for ss integration
    const loginSSIntegration = (action: ActionCandidate): void => {
      if (usesTransparentSsAuth(action)) {
        const integration = store.integrations.list.find(
          (integration) =>
            integration.integrationId === action.actionReference!.integrationId,
        );

        if (isTruthy(integration)) {
          const authMethod = integration.authenticationMethods.find(
            (auth) => auth.authenticationMethodId === "ss-auth",
          );

          if (isTruthy(authMethod)) {
            const authAttempt: AuthAttempt = {
              integrationId: action.actionReference!.integrationId,
              authMethod,
              target: {
                type: "action",
                hash: action.hash,
              },
            };
            void authDispatcher(
              authAttempt,
              [],
              store,
              engineClient,
              actionGroupType,
            ).then(() => {
              setLoadingStatus(false);
              updateActionDescription(action);
            });
          }
        }
      } else if (placeAfterId !== undefined && placeAfterId !== "") {
        if (action.credentialId === "") {
          action.credentialId = NO_CREDS;
        }
        updateActionDescription(action);
      } else if (findActionAuthenticationIds(action).length === 0) {
        if (action.credentialId === "") {
          action.credentialId = NO_CREDS;
        }
        updateActionDescription(action);
      }
    };

    // The action's own record source changed: it describes itself again from
    // the new source. Its template picker then lists the new table's
    // templates, and the select editor drops a picked template that is not
    // among them. Nothing else depends on this action's source, so no other
    // action is touched (BUG-22413).
    const onActionRecordsListInputChange = (action: ActionCandidate): void => {
      candidate.setActions([A.mergeAction(action)], actionGroupType);
      if (
        action.description.recordListInput?.dynamic === true &&
        !(
          action.recordList?.recordList.oneofKind === "reference" &&
          action.recordList?.recordList.reference ===
            RecordListInputConfig_Reference.NO_RECORD_LIST
        )
      ) {
        updateActionDescription(action);
      }
    };

    const onFieldInputChange = (
      input: InputCandidate,
      action: ActionCandidate,
    ): void => {
      if (
        action.actionReference?.actionId === "find-records" &&
        input.value !== undefined
      ) {
        if (input.inputId === INPUT_APPLICATION_ID) {
          const applicationId = pickValueDataString(input.value);
          if (applicationId != null) {
            candidate.refreshFindDependenciesByApplicationId(
              action,
              applicationId,
            );
          }
        }
      }

      candidate.setActions(
        [
          input.inputId === INPUT_APPLICATION_ID &&
          !ACTIONS_SKIP_MERGING.includes(action.description.actionId)
            ? A.mergeInputList(action.hash, [input])
            : A.mergeInputs(action.hash, [
                input,
                ...getDependentInputsToClear(input.inputId),
              ]),
        ],
        actionGroupType,
      );
      const inputDescription = action.description.inputs.find(
        (inputDescription) => inputDescription.inputId === input.inputId,
      );
      inputDescription?.dynamic && updateActionDescription(action);
    };

    const onRemoveFieldInput = (
      input: InputCandidate,
      action: ActionCandidate,
    ): void => {
      candidate.removeInputFromAction(action.hash, input);
    };

    const storeActionDescription = (
      actionDescription: ActionDescription,
      actionConfig: ActionConfig,
    ): void => {
      const staticInputList = R.pipe(
        store.integrations.list,
        R.find(
          (i) =>
            i.integrationId === actionConfig?.actionReference?.integrationId,
        ),
        (integration) =>
          R.find(
            integration?.actions ?? [],
            (a) => a.actionId === actionConfig?.actionReference?.actionId,
          ),
        (a) => a?.inputs ?? [],
      );

      const mergedInputs = A.mergeStaticAndDynamicInputDescriptions(
        staticInputList,
        actionDescription.inputs,
      );

      candidate.setActions(
        [
          A.ejectEmptyInputs(actionHash),
          A.mergeActionDescription(actionHash, {
            ...actionDescription,
            inputs: mergedInputs,
          }),
          A.mergeInputDescriptionList(actionHash, mergedInputs),
        ],
        actionGroupType,
      );
    };

    useEffect(() => {
      if (actionHash) {
        candidate.setActionGroupByActionHash(actionHash);
      }
      return () => candidate.clearCurrentAction();
    }, [actionHash]);

    // login SS Integration
    useEffect(() => {
      const removeReaction = reaction(
        (): string => {
          return (
            R.find(candidate.allActions, (a) => a.hash === actionHash)
              ?.credentialId ?? ""
          );
        },
        (credentialId) => {
          const action = R.find(
            candidate.allActions,
            (a) => a.hash === actionHash,
          );
          if (R.isTruthy(action) && actionGroupType && credentialId === "") {
            loginSSIntegration(action);
          }
        },
        { fireImmediately: true },
      );
      return () => removeReaction();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionHash, actionGroupType, placeAfterId]);

    useEffect(() => {
      const removeReaction = reaction(
        () =>
          R.find(candidate.allActions, (a) => a.hash === actionHash)
            ?.credentialId ?? "",
        (credentialId) => {
          const action = R.find(
            candidate.allActions,
            (a) => a.hash === actionHash,
          );
          if (
            !usesTransparentSsAuth(action) &&
            actionGroupType &&
            !isCredentialIdEmpty(credentialId)
          ) {
            updateActionDescription(action!);
          }
        },
        { fireImmediately: false },
      );
      return () => removeReaction();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionHash, actionGroupType]);

    useEffect(() => {
      const actionConfig = candidate.allActions.find(
        (a) => a.hash === actionHash,
      ) as ActionCandidate;

      const integration = store.integrations.list.find(
        (item) =>
          item.integrationId === actionConfig?.actionReference?.integrationId,
      );

      if (
        integration !== undefined &&
        integration.authenticationMethods.length > 0
      ) {
        setPlaceAfterId(integration.authenticationMethods[0].placeAfterId);
      }
    }, [candidate.allActions]);

    // Set initial fields
    useEffect(() => {
      const actionConfig = candidate.allActions.find(
        (a) => a.hash === actionHash,
      );
      if (
        actionConfig === undefined ||
        !actionGroupType ||
        actionConfig.inputs.length !== 0
      ) {
        return;
      }
      const integration = store.integrations.list.find(
        (item) =>
          item.integrationId === actionConfig.actionReference?.integrationId,
      );
      const actionDescription = integration?.actions.find(
        (item) => item.actionId === actionConfig.actionReference?.actionId,
      );
      if (actionDescription === undefined) {
        return;
      }
      const inputs = buildInitialInputs(actionDescription, {
        [INPUT_SOLUTION_ID]: store.config.solutionId,
        [INPUT_APPLICATION_ID]:
          actionConfig.deletedError === true
            ? undefined
            : store.applications.selectedApplicationId,
      });
      if (inputs.length === 0) {
        return;
      }
      storeActionDescription(
        withStoreSelectOptions(
          actionDescription,
          store.solutions.list,
          store.config.solutionId ? store.applications.list : [],
        ),
        actionConfig,
      );
      candidate.setActions(
        [A.mergeInputs(actionHash, inputs)],
        actionGroupType,
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionHash, actionGroupType, JSON.stringify(actions)]);

    useEffect(() => {
      const action = R.find(candidate.allActions, (a) => a.hash === actionHash);
      if (action !== undefined) {
        const applicationInput = action.inputs.find(
          (input) => input.inputId === INPUT_APPLICATION_ID,
        );
        const selectedApplicationId =
          applicationInput?.value !== undefined
            ? pickValueDataString(applicationInput?.value)
            : undefined;
        const selectedApplication = applications.find(
          (a) => a.id === selectedApplicationId,
        );
        if (selectedApplication !== undefined) {
          let solutionInput = action.inputs.find(
            (input) => input.inputId === INPUT_SOLUTION_ID,
          );
          if (solutionInput !== undefined) {
            const hasSolutionValue = pickValueDataString(solutionInput.value!);
            if (
              hasSolutionValue === undefined &&
              selectedApplication.solution !== undefined
            ) {
              solutionInput = {
                ...solutionInput,
                value: {
                  values: [
                    {
                      value: {
                        oneofKind: "string",
                        string: selectedApplication.solution,
                      },
                    },
                  ],
                },
              };
              candidate.setActions(
                [A.mergeInputs(actionHash, [solutionInput])],
                actionGroupType,
              );
            }
          }
        }
      }
    }, [actionHash, JSON.stringify(actions)]);

    const needToShowError = (hash: string): boolean => {
      const targetAction = actions.find(
        (actionCandidate) => actionCandidate.hash === hash,
      );

      if (targetAction === undefined) {
        return candidate.isActionSubmitted(hash);
      }

      return shouldShowActionInputValidationErrors(
        candidate,
        targetAction,
        automations.isAllDynamicDescriptionsLoaded(),
      );
    };

    const action: ActionCandidate | undefined = actions.find(
      (actionCandidate) => actionCandidate.hash === actionHash,
    );

    useEffect(() => {
      // potentially we would need to add more actionIds here
      if (action?.actionReference?.actionId === Integrations.ai) {
        // sort inputs by position in description
        if (action !== undefined && action.description.inputs.length > 0) {
          action.inputs.sort(
            (a, b) =>
              action.description.inputs.findIndex(
                (description) => description.inputId === a.inputId,
              ) -
              action.description.inputs.findIndex(
                (description) => description.inputId === b.inputId,
              ),
          );
        }
      }
    }, [action]);

    useEffect(() => {
      if (action?.needToUpdateDynamicDescription) {
        updateActionDescription(action);
        candidate.setNeedToUpdateDynamicDescription(action, false);
      }
    }, [action?.needToUpdateDynamicDescription]);

    const onRemoveCredential = (credentialId: string): void => {
      void engineClient
        .deleteCredential({
          credentialId,
          solutionId: store.config.solutionId,
        })
        .then(() => {
          store.credentials.removeItem(credentialId);
          void engineClient
            .getAutomationList(
              store.config.solutionId,
              automations.pageSize,
              "",
            )
            .then((automations) => {
              store.automations.replaceList(
                automations.automations,
                automations.nextPageToken,
              );
            });
          if (R.isTruthy(action)) {
            if (action.credentialId === credentialId) {
              candidate.setActions(
                [A.mergeCredentialId(action.hash, "")],
                actionGroupType,
              );
            }
          }
        });
    };

    if (R.isTruthy(action)) {
      return (
        <ActionSectionWrapper theme={{ accessError: action.accessError }}>
          <SectionHeading>
            {t("automation-form.action-settings")}
          </SectionHeading>
          <StyledIconCloseContainer
            onClick={() => {
              candidate.setActions(
                [A.ejectAction(action.hash)],
                actionGroupType,
              );
            }}
          />

          {/* Select action  */}
          <ActionSelectorMenu
            onChangeAction={onChangeAction}
            action={action}
            isOpen={isSelectOpened}
            onClick={onClickSelect}
            onClickOutside={onClickOutsideSelect}
            onClickTest={onClickTest}
            path={path}
          />
          {path && (
            <ActionTestSection
              action={action}
              onClickTest={onClickTest}
              path={path}
            />
          )}
          {!path &&
            Boolean(action.actionReference?.integrationId) &&
            Boolean(action.actionReference?.actionId) && (
              <>
                {/* Select credential */}
                {!usesTransparentSsAuth(action) &&
                  findActionAuthenticationIds(action).length > 0 &&
                  (placeAfterId === undefined || placeAfterId === "") && (
                    <ActionCredentialSelector
                      action={action}
                      descriptions={action.description.inputs}
                      needToShowError={needToShowError}
                      setAuthAttempt={setAuthAttempt}
                      onRemove={onRemoveCredential}
                    ></ActionCredentialSelector>
                  )}

                {!loading && automations.isAllDynamicDescriptionsLoaded() && (
                  <>
                    <StyledActionInputsContainer theme={{ loading }}>
                      <AlwaysFieldList
                        loading={loading}
                        action={action}
                        onInputChange={onFieldInputChange}
                        afterConditions={false}
                        afterRecordListInput={false}
                        onRemove={onRemoveCredential}
                        setAuthAttempt={setAuthAttempt}
                      />
                    </StyledActionInputsContainer>

                    {action.description.recordListInput !== undefined && (
                      <RecordsSourceEditor
                        action={action}
                        onChange={onActionRecordsListInputChange}
                      />
                    )}

                    <StyledActionInputsContainer theme={{ loading }}>
                      <AlwaysFieldList
                        loading={loading}
                        action={action}
                        onInputChange={onFieldInputChange}
                        afterConditions={false}
                        afterRecordListInput={true}
                        onRemove={onRemoveCredential}
                        setAuthAttempt={setAuthAttempt}
                      />
                    </StyledActionInputsContainer>

                    {/* Group inputs */}
                    <GroupFieldList
                      loading={loading}
                      action={action}
                      onInputChange={onFieldInputChange}
                      onRemoveFieldInput={onRemoveFieldInput}
                      afterConditions={false}
                    />
                  </>
                )}
                {!loading && automations.isAllDynamicDescriptionsLoaded() && (
                  <ConditionsForm
                    isSubmitted={needToShowError(action.hash)}
                    candidate={candidate}
                    action={action}
                    integrations={store.integrations.list}
                  />
                )}
                {!loading && automations.isAllDynamicDescriptionsLoaded() && (
                  <>
                    <StyledActionInputsContainer theme={{ loading }}>
                      <AlwaysFieldList
                        loading={loading}
                        action={action}
                        onInputChange={onFieldInputChange}
                        afterConditions={true}
                        afterRecordListInput={true}
                        onRemove={onRemoveCredential}
                        setAuthAttempt={setAuthAttempt}
                      />
                    </StyledActionInputsContainer>
                    {/* Group inputs */}
                    <GroupFieldList
                      loading={loading}
                      action={action}
                      onInputChange={onFieldInputChange}
                      onRemoveFieldInput={onRemoveFieldInput}
                      afterConditions={true}
                    />
                  </>
                )}
                {Boolean(action.credentialId) &&
                  (!action.description.actionId ||
                    !automations.isAllDynamicDescriptionsLoaded()) && (
                    <Skeleton />
                  )}
              </>
            )}
        </ActionSectionWrapper>
      );
    }
    return null;
  },
);
ActionSection.displayName = "ActionSection";
